import { StorageKeys } from "../utils/storage/storage";
import ModuleStorageKeys from "./storageKeys";
import { DriverSafetyActionTypes, IDriverSafetyDataStore } from "./driverSafety/store/types";
import { defaultDriverSafetyData, driverSafety } from "./driverSafety/store/reducers";
import { InspectionActionTypes, IInspectionStore } from "./inspections/store/types";
import { defaultInspection, inspection } from "./inspections/store/reducers";
import { CoachingActionTypes, ICoachingStore } from "./coaching/store/types";
import { defaultCoaching, coaching } from "./coaching/store/reducers";
import { HosDisplayPreferencesActionTypes, IHosDisplayPreferences } from "./hos/store/types";
import { defaultHosDisplayPreferences, hosDisplayPreferences } from "./hos/store/reducers";

// ###############################################################################################
// ||                                                                                           ||
// || Define the interfaces/types representing the reducers & actions for Geotab Drive Modules. ||
// || These are automatically picked up & mixed into the application's global reducer in        ||
// || drive/app/store/reducers.ts                                                               ||
// ||                                                                                           ||
// ###############################################################################################

export interface IModuleReducer {
    driverSafety: IIdDictionary<IDriverSafetyDataStore[]>;
    inspection: IInspectionStore;
    coaching: IIdDictionary<ICoachingStore>;
    hosDisplayPreferences: IHosDisplayPreferences;
    // otherModule: IOtherModuleStore
    // otherOtherModule: IOtherOtherModuleStore
    // ...
}

export type ModuleActions = DriverSafetyActionTypes | InspectionActionTypes | CoachingActionTypes | HosDisplayPreferencesActionTypes; // && IOtherModuleActionTypes, IOtherOtherModuleActionTypes...

// ###############################################################################################
// ||                                                                                           ||
// || It is possible (but not required) to write your data store to localStorage. The purpose   ||
// || of this is to allow data to persist between app launches (e.g. if the window is refreshed ||
// || or the app is closed and re-opened). If your store is not written to localStorage, it     ||
// || will operate temporally (on a per-instance basis).                                        ||
// ||                                                                                           ||
// || If you want to write your store to localStorage, you must add a storage key to            ||
// || modules/storageKeys, include an entry in this 'reducersToWriteToStorageDictionary', and   ||
// || make it so the store's entry in 'moduleStateData' tries reading from localStorage upon    ||
// || initialization.                                                                           ||
// ||                                                                                           ||
// || Please see the section in app/utils/session.ts titled                                     ||
// || "Data "Clear" actions for Geotab Drive Modules stores." to make calls to clear your       ||
// || reducer's data on session destroy (logout).                                               ||
// ||                                                                                           ||
// ###############################################################################################

export const moduleReducersToWriteToStorage = {
    driverSafety: { types: DriverSafetyActionTypes },
    inspection: { types: InspectionActionTypes },
    coaching: { types: CoachingActionTypes },
    hosDisplayPreferences: { types: HosDisplayPreferencesActionTypes }
};

export async function getModuleStateData(getStoredDataCallback: <T>(key: StorageKeys | ModuleStorageKeys, defaultValue: T) => Promise<T>): Promise<DeepReadonly<IModuleReducer>> {
    return {
        driverSafety: await getStoredDataCallback<IIdDictionary<IDriverSafetyDataStore[]>>(ModuleStorageKeys.DRIVER_SAFETY_STORAGE_KEY, defaultDriverSafetyData),
        inspection: await getStoredDataCallback<IInspectionStore>(ModuleStorageKeys.INSPECTION_STORAGE_KEY, defaultInspection),
        coaching: await getStoredDataCallback<IIdDictionary<ICoachingStore>>(ModuleStorageKeys.COACHING_STORAGE_KEY, defaultCoaching),
        hosDisplayPreferences: await getStoredDataCallback<IHosDisplayPreferences>(ModuleStorageKeys.HOS_DISPLAY_PREFERENCES_STORAGE_KEY, defaultHosDisplayPreferences)
        // otherModule: defaultOtherModuleData
        // otherOtherModule: defaultOtherOtherModuleData
        // ...
    };
}

export let moduleReducers = {
    driverSafety,
    inspection,
    coaching,
    hosDisplayPreferences
    // otherModule
    // otherOtherModule
    // ...
};
